import { api } from './index';
import { MEMBERS_TABLE_TAG } from './userApi';

export interface CohortTag {
  id: string;
  name: string;
}

export interface CompanyCohortTag extends CohortTag {
  memberCount: number;
}

interface CohortTagMemberRequest {
  companyId: string;
  tagId: string;
  userId: string;
}

const tagsUrl = (companyId: string) => `/companies/${companyId}/cohort_tags`;

// Tag changes refetch only the members-table rows, not every 'User' query.
export const cohortTagApi = api.injectEndpoints({
  endpoints: (builder) => ({
    addMemberCohortTag: builder.mutation<void, CohortTagMemberRequest>({
      invalidatesTags: ['CohortTag', MEMBERS_TABLE_TAG],
      query: ({ companyId, tagId, userId }) => ({
        method: 'PUT',
        url: `${tagsUrl(companyId)}/${tagId}/members/${userId}`,
      }),
    }),
    createCohortTag: builder.mutation<
      CohortTag,
      { companyId: string; name: string }
    >({
      invalidatesTags: ['CohortTag'],
      query: ({ companyId, name }) => ({
        data: { name },
        method: 'POST',
        url: tagsUrl(companyId),
      }),
    }),
    deleteCohortTag: builder.mutation<
      void,
      { companyId: string; tagId: string }
    >({
      invalidatesTags: ['CohortTag', MEMBERS_TABLE_TAG],
      query: ({ companyId, tagId }) => ({
        method: 'DELETE',
        url: `${tagsUrl(companyId)}/${tagId}`,
      }),
    }),
    getCompanyCohortTags: builder.query<CompanyCohortTag[], string>({
      providesTags: ['CohortTag'],
      query: (companyId) => ({ method: 'GET', url: tagsUrl(companyId) }),
    }),
    removeMemberCohortTag: builder.mutation<void, CohortTagMemberRequest>({
      invalidatesTags: ['CohortTag', MEMBERS_TABLE_TAG],
      query: ({ companyId, tagId, userId }) => ({
        method: 'DELETE',
        url: `${tagsUrl(companyId)}/${tagId}/members/${userId}`,
      }),
    }),
    renameCohortTag: builder.mutation<
      CohortTag,
      { companyId: string; name: string; tagId: string }
    >({
      invalidatesTags: ['CohortTag', MEMBERS_TABLE_TAG],
      query: ({ companyId, name, tagId }) => ({
        data: { name },
        method: 'PATCH',
        url: `${tagsUrl(companyId)}/${tagId}`,
      }),
    }),
  }),
});

export const {
  useAddMemberCohortTagMutation,
  useCreateCohortTagMutation,
  useDeleteCohortTagMutation,
  useGetCompanyCohortTagsQuery,
  useRemoveMemberCohortTagMutation,
  useRenameCohortTagMutation,
} = cohortTagApi;
